/**
 * Funnels list. Presentational: FunnelStudio passes real data, the DEV states
 * gallery passes Paper's mocks.
 *
 * Paper → "Heatmaps, Funnels + Attribution": "Funnels · List · 1440",
 * "Funnels · List · Loading", "Funnels · None saved". Answer headline, a
 * three-tile strip and the funnel table, in one well. Orange is absent: the
 * answer row's conversion is the warning tone, every other number is ink.
 */
import * as React from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowRight01Icon,
  Cursor01Icon,
  File01Icon,
  MoreHorizontalIcon,
  Note01Icon,
} from "@hugeicons/core-free-icons"
import { Skeleton } from "@/components/ui/skeleton"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { formatConversion, type FunnelChip, type FunnelRowResult } from "./funnelListModel"

export type FunnelLibraryTile = {
  label: string
  value: string | null
  /** Small ink delta beside the value, e.g. "+15%". */
  delta?: string | null
  sub: string | null
}

export type FunnelLibraryRow = {
  id: string
  name: string
  chips: FunnelChip[]
  result: FunnelRowResult | null
  /** Result still loading: numbers render as skeletons. */
  resultLoading?: boolean
  updated: string | null
  /** The page answer: conversion renders in the warning tone. */
  isAnswer?: boolean
  /** Built from the site's traffic, not saved yet: opening it lets them edit and save. */
  suggested?: boolean
}

export type FunnelLibraryViewProps = {
  loading?: boolean
  title: string
  subtitle: string
  tiles: [FunnelLibraryTile, FunnelLibraryTile, FunnelLibraryTile]
  rows: FunnelLibraryRow[]
  onOpen?: (id: string) => void
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
  /** Rendered in the table body when there are no rows. */
  empty?: React.ReactNode
  /** Banners (plan limit, suggestions) between the headline and the well. */
  children?: React.ReactNode
}

const COL = {
  conversion: "w-[90px] shrink-0",
  visitors: "w-[90px] shrink-0",
  updated: "hidden w-20 shrink-0 justify-end text-right sm:flex",
  chevron: "w-[14px] shrink-0",
}

const LOADING_ROWS: Array<{ name: number; chips: number[] }> = [
  { name: 133, chips: [35, 74, 105, 98] },
  { name: 94, chips: [35, 67, 110] },
  { name: 150, chips: [35, 67] },
  { name: 188, chips: [142, 85, 105] },
  { name: 141, chips: [70, 92] },
]

export function FunnelLibraryView({
  loading = false,
  title,
  subtitle,
  tiles,
  rows,
  onOpen,
  onEdit,
  onDelete,
  empty,
  children,
}: FunnelLibraryViewProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        {loading ? (
          <>
            <div className="flex h-7 items-center">
              <Skeleton className="h-7 w-[60%]" />
            </div>
            <div className="flex h-4 items-center">
              <Skeleton className="h-[13px] w-[38%]" />
            </div>
          </>
        ) : (
          <>
            <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{title}</h1>
            <p className="text-[13px] leading-4 text-muted-foreground">{subtitle}</p>
          </>
        )}
      </div>

      {children}

      <div className="well flex flex-col gap-2">
        {/* Tiles */}
        <div className="surface-raised flex flex-col overflow-clip sm:flex-row">
          {tiles.map((tile, i) => (
            <div
              key={tile.label}
              className={cn(
                "flex min-w-0 flex-1 basis-0 flex-col gap-2 px-5 py-4",
                i < tiles.length - 1 && "border-b border-foreground/[0.08] sm:border-r sm:border-b-0",
              )}
            >
              <span className="text-xs leading-4 text-muted-foreground">{tile.label}</span>
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="flex h-[34px] items-center">
                    <Skeleton className={cn("h-7", i === 0 ? "w-11" : "w-[78px]")} />
                  </div>
                  {tile.delta !== undefined && <Skeleton className="mt-4 h-3 w-[27px]" />}
                </div>
              ) : (
                <div className="flex items-baseline gap-2">
                  <span className="text-[28px] font-semibold leading-[34px] tracking-[-0.02em] tabular-nums text-foreground">
                    {tile.value ?? "–"}
                  </span>
                  {tile.delta && (
                    <span className="text-xs font-medium leading-4 tabular-nums text-foreground">{tile.delta}</span>
                  )}
                </div>
              )}
              {loading ? (
                <div className="flex h-4 items-center">
                  <Skeleton className={cn("h-3", ["w-[114px]", "w-full max-w-[302px]", "w-full max-w-[235px]"][i])} />
                </div>
              ) : (
                <span className="truncate text-xs leading-4 text-muted-foreground">{tile.sub ?? " "}</span>
              )}
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="surface-raised flex flex-col overflow-clip" role="table" aria-label="Saved funnels">
          <div
            role="row"
            className="flex h-10 shrink-0 items-center gap-4 border-b border-foreground/[0.08] bg-muted/50 px-5 text-xs leading-4 text-muted-foreground"
          >
            <span role="columnheader" className="min-w-0 flex-1 basis-0">Funnel</span>
            <span role="columnheader" className={cn(COL.conversion, "hidden text-right sm:block")}>Conversion</span>
            <span role="columnheader" className={cn(COL.visitors, "hidden text-right sm:block")}>Visitors</span>
            <span role="columnheader" className={COL.updated}>Updated</span>
            <span aria-hidden className={COL.chevron} />
          </div>

          {loading
            ? LOADING_ROWS.map((r, i) => (
                <div
                  key={i}
                  role="row"
                  className={cn(
                    "flex h-20 shrink-0 items-center gap-4 px-5",
                    i < LOADING_ROWS.length - 1 && "border-b border-foreground/[0.08]",
                  )}
                >
                  <div className="flex min-w-0 flex-1 basis-0 flex-col gap-2">
                    <div className="flex h-[18px] items-center">
                      <Skeleton className="h-3.5" style={{ width: r.name }} />
                    </div>
                    <div className="flex items-center gap-5 overflow-hidden">
                      {r.chips.map((w, j) => (
                        <Skeleton key={j} className="h-[22px] shrink-0 rounded-md" style={{ width: w }} />
                      ))}
                    </div>
                  </div>
                  <RowNumberSkeleton label="converted" className={COL.conversion} bar="h-[15px] w-[34px]" />
                  <RowNumberSkeleton label="entered" className={COL.visitors} bar="h-3.5 w-[39px]" />
                  <div className={COL.updated}>
                    <Skeleton className="h-3 w-10" />
                  </div>
                  <HugeiconsIcon icon={ArrowRight01Icon} size={14} className={cn(COL.chevron, "text-foreground/35")} />
                </div>
              ))
            : rows.length === 0
              ? empty
              : rows.map((row, i) => (
                  <FunnelRow
                    key={row.id}
                    row={row}
                    last={i === rows.length - 1}
                    onOpen={onOpen}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
        </div>
      </div>
    </div>
  )
}

function RowNumberSkeleton({ label, className, bar }: { label: string; className?: string; bar: string }) {
  return (
    <div className={cn(className, "flex flex-col items-end gap-0.5")}>
      <div className="flex h-[18px] items-center">
        <Skeleton className={bar} />
      </div>
      <span className="text-xs leading-4 text-muted-foreground">{label}</span>
    </div>
  )
}

const CHIP_ICON = {
  page_view: File01Icon,
  click: Cursor01Icon,
  form_submission: Note01Icon,
} as const

function FunnelRow({
  row,
  last,
  onOpen,
  onEdit,
  onDelete,
}: {
  row: FunnelLibraryRow
  last: boolean
  onOpen?: (id: string) => void
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
}) {
  const open = onOpen ? () => onOpen(row.id) : undefined
  // A suggestion is not saved yet: nothing to edit or delete from the list.
  const hasMenu = !row.suggested && Boolean(onEdit || onDelete)
  return (
    <div
      role="row"
      tabIndex={open ? 0 : undefined}
      onClick={open}
      onKeyDown={open ? (e) => e.key === "Enter" && open() : undefined}
      className={cn(
        "group relative flex min-h-20 shrink-0 flex-wrap items-center gap-x-4 gap-y-3 px-5 py-3 transition-colors focus-visible:bg-muted/60 focus-visible:outline-none sm:flex-nowrap",
        !last && "border-b border-foreground/[0.08]",
        open && "cursor-pointer hover:bg-muted/40",
      )}
    >
      <div role="cell" className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-1 sm:basis-0">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium leading-[18px] text-foreground">{row.name}</span>
          {row.suggested && (
            <Badge variant="secondary" className="h-5 shrink-0 px-1.5 text-[10px] font-medium">
              Suggested
            </Badge>
          )}
        </span>
        <div className="flex flex-wrap items-center gap-[5px]">
          {row.chips.map((chip, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <HugeiconsIcon icon={ArrowRight01Icon} size={10} strokeWidth={2.6} className="shrink-0 text-foreground/30" />
              )}
              <span className="inline-flex h-[22px] max-w-[220px] items-center gap-[5px] rounded-md bg-muted px-[7px]">
                <HugeiconsIcon icon={CHIP_ICON[chip.type]} size={11} className="shrink-0 text-muted-foreground" />
                <span className="truncate text-xs leading-4 text-foreground">{chip.label}</span>
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <span aria-hidden className="flex-1 sm:hidden" />
      <div role="cell" className={cn(COL.conversion, "flex flex-col items-end gap-0.5")}>
        {row.resultLoading ? (
          <div className="flex h-[18px] items-center">
            <Skeleton className="h-3.5 w-[34px]" />
          </div>
        ) : (
          <span
            className={cn(
              "text-[15px] font-semibold leading-[18px] tabular-nums",
              row.isAnswer ? "text-warning" : row.result ? "text-foreground" : "text-foreground/40",
            )}
          >
            {row.result ? formatConversion(row.result.conversion) : "–"}
          </span>
        )}
        <span className="text-xs leading-4 text-muted-foreground">converted</span>
      </div>

      <div role="cell" className={cn(COL.visitors, "flex flex-col items-end gap-0.5")}>
        {row.resultLoading ? (
          <div className="flex h-[18px] items-center">
            <Skeleton className="h-3.5 w-[34px]" />
          </div>
        ) : (
          <span
            className={cn(
              "text-sm font-medium leading-[18px] tabular-nums",
              row.result ? "text-foreground" : "text-foreground/40",
            )}
          >
            {row.result ? row.result.entered.toLocaleString("en-US") : "–"}
          </span>
        )}
        <span className="text-xs leading-4 text-muted-foreground">entered</span>
      </div>

      <div
        role="cell"
        className={cn(
          COL.updated,
          "text-xs leading-4 text-muted-foreground",
          hasMenu && "transition-opacity group-hover:opacity-0 group-focus-within:opacity-0",
        )}
      >
        {row.suggested ? "Not saved" : row.updated ?? "–"}
      </div>

      <HugeiconsIcon icon={ArrowRight01Icon} size={14} strokeWidth={2.2} className={cn(COL.chevron, "text-foreground/35")} />

      {hasMenu && (
        <div className="absolute right-10 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-state=open]]:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-label={`Actions for ${row.name}`}
                className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <HugeiconsIcon icon={MoreHorizontalIcon} size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onEdit && <DropdownMenuItem onClick={() => onEdit(row.id)}>Edit steps</DropdownMenuItem>}
              {onDelete && (
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(row.id)}>
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
}

/** Empty table body: Paper "Funnels · None saved". */
export function FunnelLibraryEmpty({ actions }: { actions?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center px-4 pb-[52px] pt-14">
      <div className="mb-4 flex size-10 items-center justify-center rounded-lg bg-muted/50 ring-1 ring-border">
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden className="text-foreground/35">
          <path d="M4 5h16M7 12h10M10 19h4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      </div>
      <h2 className="mb-1.5 text-center text-sm font-medium leading-[18px] text-foreground">No funnels yet</h2>
      <p className="max-w-[440px] text-center text-[13px] leading-5 text-muted-foreground">
        A funnel shows the step where visitors give up on the way to signing up. Start from suggestions built on your
        top paths.
      </p>
      {actions && <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{actions}</div>}
    </div>
  )
}
